import { useEffect, useState } from "react";
import { loadCoachProfile } from "../lib/coachProfile";
import { coachPageState, coachSetupCopy, type CoachPageState } from "../lib/coachSetup";
import { href } from "../lib/route";
import { COMMUNITY_INCLUDED } from "../lib/communityFeature";

/* THE PROMPT A NEW WEB COACH LANDS ON — the port of the phone's `CoachSetupPrompt`.
 *
 * 🔴 **ITS SUBJECT IS A REAL PERSON, MEASURED 2026-09-22:** William Soo signed up on the web, built
 * 145 exercises and 4 workouts, and has no `coach_profiles` row. He is invisible in the directory
 * and nothing in the browser has ever mentioned it. The full measurement is in `lib/coachSetup.ts`.
 *
 * ⚠️ **IT RETIRES ITSELF, WHICH IS WHAT MAKES IT A PROMPT AND NOT A BANNER** — the phone's own
 * words. The only states it draws in are "not started" and "not published"; the moment the page is
 * live it is gone for ever, with no dismiss control to remember and nothing stored about having
 * shown it. A prompt that needs a "don't show again" is a prompt that outstayed its welcome.
 *
 * ⚠️ **IT IS NOT AN ERROR AND MUST NOT LOOK LIKE ONE.** Nothing has gone wrong — the coach simply
 * has not done a step nobody told them about. So it is a `.ht-panel` and a quiet link, never the
 * screen's one `.ht-button-primary`: this is a surface with an Invite button on it, and a setup
 * notice that shouts louder than the page's actual job is a notice people learn to scroll past.
 *
 * ⚠️ **AND IT DRAWS NOTHING WHILE THE ANSWER IS IN FLIGHT.** `unknown` is its own state (see
 * `coachPageState`): flashing "set up your coaching page" at a coach who published months ago is a
 * lie about their account, and arriving a beat late costs nothing.
 */
export function CoachSetupPrompt({ coachId }: { coachId: string }) {
  const [state, setState] = useState<CoachPageState>("unknown");

  useEffect(() => {
    let cancelled = false;
    void loadCoachProfile(coachId).then((result) => {
      if (cancelled) return;
      // ⚠️ A FAILED READ STAYS `unknown` AND THE CARD STAYS AWAY. A read that errored is not
      // evidence the coach has no page — treating it as "absent" would tell a published coach
      // their page is missing every time the network hiccuped.
      if ("error" in result) return;
      setState(coachPageState({ exists: result.exists, isPublished: result.profile.isPublished }));
    });
    return () => { cancelled = true; };
  }, [coachId]);

  const copy = coachSetupCopy(state);
  // W120: the prompt sets up the DIRECTORY's page, so it leaves with the Community (`COMMUNITY_INCLUDED`).
  if (copy === null || !COMMUNITY_INCLUDED) return null;

  return (
    <section className="ht-panel coach-setup" aria-labelledby="coach-setup-title">
      <h2 className="ht-body-medium coach-setup-title" id="coach-setup-title">{copy.title}</h2>
      <p className="ht-caption coach-setup-body">{copy.body}</p>
      {/* A real `<a href>`, so the back button, middle-click and keyboard all mean what they mean
          everywhere else — the same argument `AccountRow` makes for its own link. */}
      <a className="ht-link coach-setup-action" href={href({ name: "page" })}>{copy.action}</a>
    </section>
  );
}
